import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Settings, FileText } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import config from '../config';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod as bornesDeComparaison } from '../utils/periodUtils';
/**
 * Composant table modulaire pour l'analyse N-gram des termes de recherche
 * Optimisé avec React.memo et useMemo pour les performances
 */
const NgramAnalysisTable = React.memo(({
  autoCharts,
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  performanceMaxData,
  campaignFilter = 'all',
  cpcTierFilter = null,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  openNgramModal,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = '',
  token
}) => {
  // États locaux pour cette table
  const [selectedNgramSize, setSelectedNgramSize] = useState(1); // 1=unigram, 2=bigram, 3=trigram
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [previousSearchTermsData, setPreviousSearchTermsData] = useState([]);

  // Calcul de la période précédente
  const calculatePreviousPeriod = useCallback(() => {
    if (!comparisonType || !selectedPeriod) return null;

    // Les bornes de la période regardée, puis celles auxquelles on la compare. Les deux
    // calculs vivaient ici, recopiés dans une vingtaine d'écrans : ils sont dans
    // `utils/periodUtils`, seul endroit à savoir ce que « hier » veut dire un jour de
    // changement d'heure.
    const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
    if (!plage) return null;

    return bornesDeComparaison(plage.startDate, plage.endDate, comparisonType);
  }, [comparisonType, selectedPeriod, customStartDate, customEndDate]);


  // Affichage de la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('search_terms_ngram', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Extraction et traitement des données combinées (memoized)
  const combinedSearchTermsData = useMemo(() => {
    if (!selectedAccount) {
      console.log(`❌ [NGRAM ANALYSIS] Pas de compte sélectionné`);
      return [];
    }

    // Récupérer les données Search/Shopping du backend
    const searchTermsBackendData = (autoCharts && autoCharts.rawData && selectedAccount) ? 
      autoCharts.rawData[selectedAccount.name] : null;

    let searchShoppingTerms = [];
    if (searchTermsBackendData) {
      // Format searchTermsData.terms
      if (searchTermsBackendData.searchTermsData && searchTermsBackendData.searchTermsData.terms) {
        searchShoppingTerms = searchTermsBackendData.searchTermsData.terms;
      }
      // Format searchTerms.searchTerms
      else if (searchTermsBackendData.searchTerms && searchTermsBackendData.searchTerms.searchTerms) {
        searchShoppingTerms = searchTermsBackendData.searchTerms.searchTerms;
      }
      // Format searchTerms direct (array)
      else if (searchTermsBackendData.searchTerms && Array.isArray(searchTermsBackendData.searchTerms)) {
        searchShoppingTerms = searchTermsBackendData.searchTerms;
      }
    }

    // Récupérer les données Performance Max
    let performanceMaxTerms = [];
    if (performanceMaxData && selectedAccount) {
      const performanceMaxAccountData = performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name];
      if (performanceMaxAccountData && performanceMaxAccountData.searchTerms) {
        if (Array.isArray(performanceMaxAccountData.searchTerms)) {
          performanceMaxTerms = performanceMaxAccountData.searchTerms.map(term => ({
            ...term,
            source: 'Performance Max',
            searchTerm: term.searchTerm || term.term || 'Non spécifié'
          }));
        }
      }
    }

    // Combiner toutes les données en préservant les informations de campagne
    const allSearchTermsRaw = [
      ...searchShoppingTerms.map(term => ({ 
        ...term, 
        source: term.source || 'Search/Shopping',
        campaign: term.campaign || 'Non spécifié',
        campaignId: term.campaignId || null,
        adGroup: term.adGroup || null,
        adGroupId: term.adGroupId || null
      })),
      ...performanceMaxTerms
    ];

    // Appliquer le filtre par campagne
    const filteredSearchTermsRaw = campaignFilter === 'all' 
      ? allSearchTermsRaw 
      : allSearchTermsRaw.filter(term => term.campaignId === campaignFilter);

    console.log(`🎯 [NGRAM ANALYSIS] Filtre campagne "${campaignFilter}" appliqué: ${filteredSearchTermsRaw.length} termes (sur ${allSearchTermsRaw.length} total)`);

    // Appliquer le filtre par tranche de CPC si actif
    let cpcFilteredTerms = filteredSearchTermsRaw;
    if (cpcTierFilter && cpcTierFilter.min !== undefined && cpcTierFilter.max !== undefined) {
      cpcFilteredTerms = filteredSearchTermsRaw.filter(term => {
        const clicks = Number(term.clicks) || 0;
        const cost = Number(term.cost) || 0;
        const cpc = clicks > 0 ? (cost / clicks) : 0;
        
        // Filtrer par tranche de CPC
        return cpc >= cpcTierFilter.min && cpc <= cpcTierFilter.max;
      });
      
      console.log(`🎯 [CPC-TIER-FILTER] Filtre CPC ${cpcTierFilter.min}€-${cpcTierFilter.max}€ appliqué: ${cpcFilteredTerms.length} termes (sur ${filteredSearchTermsRaw.length} après filtre campagne)`);
    }

    return cpcFilteredTerms;
  }, [autoCharts, selectedAccount, performanceMaxData, campaignFilter, cpcTierFilter]);

  // Fonctions utilitaires pour le traitement des N-grams (memoized)
  const { normalizeText, generateNgrams } = useMemo(() => {
    const normalizeText = (text) => {
      if (!text) return '';
      
      const normalized = text
        .toLowerCase()
        .normalize('NFD') // Décomposer les caractères accentués
        .replace(/[\u0300-\u036f]/g, '') // Supprimer les diacritiques
        .replace(/[^\w\s]/g, '') // Supprimer la ponctuation (pas les tirets dans les mots)
        .replace(/\s+/g, ' ') // Normaliser les espaces
        .trim();
      
      return normalized;
    };

    const generateNgrams = (text, n) => {
      if (!text || typeof text !== 'string') return [];
      
      const normalizedText = normalizeText(text);
      const words = normalizedText
        .split(/\s+/)
        .filter(word => word.length > 0);
      
      if (words.length < n) return [];
      
      const ngrams = [];
      for (let i = 0; i <= words.length - n; i++) {
        const ngram = words.slice(i, i + n).join(' ');
        ngrams.push(ngram);
      }
      
      return ngrams;
    };

    return { normalizeText, generateNgrams };
  }, []);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousPeriodData = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount || !token) {
        setPreviousSearchTermsData([]);
        return;
      }

      const previousDates = calculatePreviousPeriod();
      if (!previousDates) {
        setPreviousSearchTermsData([]);
        return;
      }

      try {
        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousDates.startDate,
          customEndDate: previousDates.endDate
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/search-terms`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data) {
          const resultsData = response.data.searchTermsResults || response.data;
          const accountKey = Object.keys(resultsData).find(key => !key.includes('_error') && key !== 'success' && key !== 'pagination');

          if (accountKey && resultsData[accountKey]) {
            const data = resultsData[accountKey];
            let terms = [];

            if (data.searchTerms && data.searchTerms.searchTerms) {
              terms = data.searchTerms.searchTerms;
            } else if (data.searchTerms && Array.isArray(data.searchTerms)) {
              terms = data.searchTerms;
            } else if (data.searchTermsData && data.searchTermsData.terms) {
              terms = data.searchTermsData.terms;
            }

            setPreviousSearchTermsData(terms);
            console.log('✅ [NGRAM-COMPARE] Données période précédente chargées:', { terms: terms.length });
          } else {
            setPreviousSearchTermsData([]);
          }
        } else {
          setPreviousSearchTermsData([]);
        }
      } catch (err) {
        console.error('❌ [NGRAM-COMPARE] Erreur chargement période précédente:', err);
        setPreviousSearchTermsData([]);
      }
    };

    loadPreviousPeriodData();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, token]);

  // Générer les N-grams à partir des données de la période précédente (memoized)
  const previousNgramMap = useMemo(() => {
    if (!compareEnabled || !previousSearchTermsData.length) return {};

    const ngramMap = {};

    previousSearchTermsData.forEach(term => {
      const searchTermText = term.searchTerm || term.term || '';
      const ngrams = generateNgrams(searchTermText, selectedNgramSize);

      ngrams.forEach(ngram => {
        if (!ngramMap[ngram]) {
          ngramMap[ngram] = {
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0,
            termCount: 0
          };
        }

        ngramMap[ngram].termCount += 1;
        ngramMap[ngram].impressions += term.impressions || 0;
        ngramMap[ngram].clicks += term.clicks || 0;
        ngramMap[ngram].cost += term.cost || 0;
        ngramMap[ngram].conversions += term.conversions || 0;
        ngramMap[ngram].conversionsValue += term.conversionsValue || 0;
      });
    });

    // Calculer les métriques dérivées pour chaque n-gram
    Object.values(ngramMap).forEach(ngram => {
      ngram.ctr = ngram.impressions > 0 ? (ngram.clicks / ngram.impressions * 100) : 0;
      ngram.cpc = ngram.clicks > 0 ? (ngram.cost / ngram.clicks) : 0;
      ngram.conversionRate = ngram.clicks > 0 ? (ngram.conversions / ngram.clicks * 100) : 0;
      ngram.cpa = ngram.conversions > 0 ? (ngram.cost / ngram.conversions) : 0;
      ngram.cos = ngram.conversionsValue > 0 ? (ngram.cost / ngram.conversionsValue * 100) : 0;
    });

    console.log('📊 [NGRAM-COMPARE] Map n-grams période précédente générée:', Object.keys(ngramMap).length, 'n-grams');
    return ngramMap;
  }, [compareEnabled, previousSearchTermsData, selectedNgramSize, generateNgrams]);

  // Analyse des N-grams (memoized)
  const ngramData = useMemo(() => {
    if (combinedSearchTermsData.length === 0) return [];

    const ngramMap = {};
    const ngramTermsMap = {}; // Pour stocker les termes associés à chaque n-gram
    
    combinedSearchTermsData.forEach(term => {
      const searchTermText = term.searchTerm || term.term || '';
      const ngrams = generateNgrams(searchTermText, selectedNgramSize);
      

      // Vérification que les n-grams correspondent à des mots complets séparés par des espaces
      const validNgrams = ngrams.filter(ngram => {
        const normalizedText = normalizeText(searchTermText);
        const wordsInTerm = normalizedText.split(/\s+/).filter(word => word.length > 0);
        
        // Pour les 1-grams, vérifier que le mot existe exactement comme mot entier
        if (selectedNgramSize === 1) {
          // Utiliser des limites de mots pour s'assurer que c'est un mot complet
          const wordBoundaryRegex = new RegExp(`\\b${ngram.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
          const isCompleteWord = wordBoundaryRegex.test(normalizedText);
          
          return isCompleteWord;
        }
        
        // Pour les n-grams multi-mots, vérifier qu'ils correspondent à une séquence de mots adjacents
        if (selectedNgramSize > 1) {
          const ngramWords = ngram.split(' ');
          
          // Trouver toutes les positions où la séquence pourrait commencer
          for (let i = 0; i <= wordsInTerm.length - ngramWords.length; i++) {
            const sequenceMatches = ngramWords.every((word, index) => 
              wordsInTerm[i + index] === word
            );
            if (sequenceMatches) {
              return true;
            }
          }
          return false;
        }
        
        return true;
      });
      
      validNgrams.forEach(ngram => {
        if (!ngramMap[ngram]) {
          ngramMap[ngram] = {
            ngram: ngram,
            termCount: 0,
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0,
            associatedTerms: [] // Pour le debug
          };
          ngramTermsMap[ngram] = [];
        }

        const ngramEntry = ngramMap[ngram];
        ngramEntry.termCount += 1;
        ngramEntry.impressions += term.impressions || 0;
        ngramEntry.clicks += term.clicks || 0;
        ngramEntry.cost += term.cost || 0;
        ngramEntry.conversions += term.conversions || 0;
        ngramEntry.conversionsValue += term.conversionsValue || 0;
        ngramEntry.associatedTerms.push(searchTermText);
        ngramTermsMap[ngram].push(searchTermText);
      });
    });


    return Object.values(ngramMap);
  }, [combinedSearchTermsData, selectedNgramSize, generateNgrams]);

  // Tri des N-grams (memoized)
  const sortedNgrams = useMemo(() => {
    if (!ngramData.length) return [];

    const sorted = [...ngramData];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'cpa') {
        // Gestion spéciale pour CPA (éviter les valeurs infinies)
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else if (sortConfig.key === 'ngram') {
        aValue = (a.ngram || '').toLowerCase();
        bValue = (b.ngram || '').toLowerCase();
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [ngramData, sortConfig]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalPages = Math.ceil(sortedNgrams.length / itemsPerPage);

  // Données paginées (memoized)
  const paginatedNgrams = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedNgrams.slice(startIndex, endIndex);
  }, [sortedNgrams, currentPage, itemsPerPage]);

  // Calcul des totaux (memoized)
  const totals = useMemo(() => {
    if (!paginatedNgrams.length) return {};

    const ngramTotals = paginatedNgrams.reduce((acc, ngram) => ({
      termCount: acc.termCount + ngram.termCount,
      impressions: acc.impressions + ngram.impressions,
      clicks: acc.clicks + ngram.clicks,
      cost: acc.cost + ngram.cost,
      conversions: acc.conversions + ngram.conversions,
      conversionsValue: acc.conversionsValue + (parseFloat(ngram.conversionsValue) || 0)
    }), {
      termCount: 0,
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0
    });
    
    // Calculer les métriques dérivées
    return {
      ...ngramTotals,
      ctr: ngramTotals.impressions > 0 ? (ngramTotals.clicks / ngramTotals.impressions * 100) : 0,
      cpc: ngramTotals.clicks > 0 ? (ngramTotals.cost / ngramTotals.clicks) : 0,
      conversionRate: ngramTotals.clicks > 0 ? (ngramTotals.conversions / ngramTotals.clicks * 100) : 0,
      cpa: ngramTotals.conversions > 0 ? (ngramTotals.cost / ngramTotals.conversions) : 0,
      cos: ngramTotals.conversionsValue > 0 ? (ngramTotals.cost / ngramTotals.conversionsValue * 100) : 0
    };
  }, [paginatedNgrams]);

  // Totaux de la période précédente (pour les n-grams affichés sur la page actuelle)
  const previousTotals = useMemo(() => {
    if (!compareEnabled || Object.keys(previousNgramMap).length === 0 || !paginatedNgrams.length) return null;

    // Calculer les totaux précédents uniquement pour les n-grams de la page actuelle
    const baseTotals = paginatedNgrams.reduce((acc, ngram) => {
      const prevNgram = previousNgramMap[ngram.ngram];
      if (!prevNgram) return acc;

      return {
        termCount: acc.termCount + (prevNgram.termCount || 0),
        impressions: acc.impressions + (prevNgram.impressions || 0),
        clicks: acc.clicks + (prevNgram.clicks || 0),
        cost: acc.cost + (prevNgram.cost || 0),
        conversions: acc.conversions + (prevNgram.conversions || 0),
        conversionsValue: acc.conversionsValue + (prevNgram.conversionsValue || 0)
      };
    }, {
      termCount: 0,
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0
    });

    // Si aucune donnée trouvée, retourner null
    if (baseTotals.impressions === 0 && baseTotals.clicks === 0 && baseTotals.cost === 0) return null;

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousNgramMap, paginatedNgrams]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => {
      const direction = prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc';
      return { key, direction };
    });
    setCurrentPage(1);
  }, []);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Handler de changement de taille N-gram memoized
  const handleNgramSizeChange = useCallback((newSize) => {
    setSelectedNgramSize(newSize);
    setCurrentPage(1);
  }, []);

  // Handler d'ouverture modal memoized
  const handleNgramClick = useCallback((ngram) => {
    // Debug: afficher les termes associés à ce n-gram
    console.log(`📊 N-gram cliqué: "${ngram.ngram}"`);
    if (ngram.associatedTerms) {
      console.log(`📋 Termes associés:`, ngram.associatedTerms);
    }
    
    if (openNgramModal) {
      openNgramModal(ngram, combinedSearchTermsData);
    }
  }, [openNgramModal, combinedSearchTermsData]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sortedNgrams.length);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4>
          <FileText size={16} />&nbsp;Analyse N-gram {selectedNgramSize === 1 ? '(Mots)' : selectedNgramSize === 2 ? '(Bigrammes)' : '(Trigrammes)'}
        </h4>
        <div className="section-header-buttons">
          <select
            value={selectedNgramSize}
            onChange={(e) => handleNgramSizeChange(parseInt(e.target.value))}
            className="form-select"
            style={{ width: 'auto' }}
          >
            <option value={1}>Mots (1-gram)</option>
            <option value={2}>Bigrammes (2-gram)</option>
            <option value={3}>Trigrammes (3-gram)</option>
          </select>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>
      
      {isVisible && (
        <div className="table-container">
          <div className="campaigns-table-wrapper">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('ngram')} className="sortable">
                    {selectedNgramSize === 1 ? 'Mot' : selectedNgramSize === 2 ? 'Bigramme' : 'Trigramme'} {sortConfig.key === 'ngram' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('termCount')} className="sortable numeric">
                    Nb termes {sortConfig.key === 'termCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricSelected('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {paginatedNgrams.length === 0 ? (
                  <tr>
                    <td colSpan="100%" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      📝 Aucun n-gram trouvé pour cette taille
                    </td>
                  </tr>
                ) : (
                  paginatedNgrams.map((ngram, index) => {
                    const ctr = ngram.impressions > 0 ? (ngram.clicks / ngram.impressions * 100) : 0;
                    const cpc = ngram.clicks > 0 ? (ngram.cost / ngram.clicks) : 0;
                    const conversionRate = ngram.clicks > 0 ? (ngram.conversions / ngram.clicks * 100) : 0;
                    const cpa = ngram.conversions > 0 ? (ngram.cost / ngram.conversions) : 0;
                    const conversionsValue = parseFloat(ngram.conversionsValue) || 0;
                    const cos = conversionsValue > 0 ? (ngram.cost / conversionsValue * 100) : 0;

                    // Données de la période précédente pour ce n-gram
                    const prevNgram = previousNgramMap[ngram.ngram];

                    return (
                      <tr key={index}>
                        <td className="search-term-name">
                          <span
                            style={{
                              color: '#1a73e8',
                              fontWeight: '500',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleNgramClick(ngram)}
                            title="Cliquer pour voir les termes de recherche associés"
                          >
                            {ngram.ngram}
                          </span>
                        </td>
                        <td className="numeric">
                          {formatNumber(ngram.termCount)}
                          {prevNgram && renderVariation(calculateVariation(ngram.termCount, prevNgram.termCount))}
                        </td>
                        {isComponentMetricSelected('impressions') && (
                          <td className="numeric">
                            {formatNumber(ngram.impressions)}
                            {prevNgram && renderVariation(calculateVariation(ngram.impressions, prevNgram.impressions))}
                          </td>
                        )}
                        {isComponentMetricSelected('ctr') && (
                          <td className="numeric">
                            {formatNumber(ctr, 2)}%
                            {prevNgram && renderVariation(calculateVariation(ctr, prevNgram.ctr))}
                          </td>
                        )}
                        {isComponentMetricSelected('clics') && (
                          <td className="numeric">
                            {formatNumber(ngram.clicks)}
                            {prevNgram && renderVariation(calculateVariation(ngram.clicks, prevNgram.clicks))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpc') && (
                          <td className="numeric">
                            €{formatNumber(cpc, 2)}
                            {prevNgram && renderVariation(calculateVariation(cpc, prevNgram.cpc), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('cout') && (
                          <td className="numeric">
                            €{formatNumber(ngram.cost, 2)}
                            {prevNgram && renderVariation(calculateVariation(ngram.cost, prevNgram.cost), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('taux_conv') && (
                          <td className="numeric">
                            {formatNumber(conversionRate, 2)}%
                            {prevNgram && renderVariation(calculateVariation(conversionRate, prevNgram.conversionRate))}
                          </td>
                        )}
                        {isComponentMetricSelected('conv') && (
                          <td className="numeric">
                            {formatNumber(ngram.conversions, 1)}
                            {prevNgram && renderVariation(calculateVariation(ngram.conversions, prevNgram.conversions))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpa') && (
                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'search_terms_ngram')}`}>
                            {cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                            {prevNgram && renderVariation(calculateVariation(cpa, prevNgram.cpa), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('valeur_conv') && (
                          <td className="numeric">
                            €{formatNumber(conversionsValue, 2)}
                            {prevNgram && renderVariation(calculateVariation(conversionsValue, prevNgram.conversionsValue))}
                          </td>
                        )}
                        {isComponentMetricSelected('cos') && (
                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'search_terms_ngram')}`}>
                            {formatNumber(cos, 1)}%
                            {prevNgram && renderVariation(calculateVariation(cos, prevNgram.cos), true)}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>

              {paginatedNgrams.length > 0 && (
                <tfoot>
                  <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <td style={{ fontWeight: '600' }}>
                      TOTAL ({paginatedNgrams.length} {selectedNgramSize === 1 ? 'mots' : selectedNgramSize === 2 ? 'bigrammes' : 'trigrammes'})
                    </td>
                    <td className="numeric" style={{ fontWeight: '600' }}>
                      {formatNumber(totals.termCount)}
                      {previousTotals && renderVariation(calculateVariation(totals.termCount, previousTotals.termCount))}
                    </td>
                    {isComponentMetricSelected('impressions') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        {formatNumber(totals.impressions)}
                        {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
                      </td>
                    )}
                    {isComponentMetricSelected('ctr') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        {formatNumber(totals.ctr, 2)}%
                        {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
                      </td>
                    )}
                    {isComponentMetricSelected('clics') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        {formatNumber(totals.clicks)}
                        {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
                      </td>
                    )}
                    {isComponentMetricSelected('cpc') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        €{formatNumber(totals.cpc, 2)}
                        {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('cout') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        €{formatNumber(totals.cost, 2)}
                        {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('taux_conv') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        {formatNumber(totals.conversionRate, 2)}%
                        {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
                      </td>
                    )}
                    {isComponentMetricSelected('conv') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        {formatNumber(totals.conversions, 1)}
                        {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
                      </td>
                    )}
                    {isComponentMetricSelected('cpa') && (
                      <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'search_terms_ngram')}`} style={{ fontWeight: '600' }}>
                        {totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}
                        {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('valeur_conv') && (
                      <td className="numeric" style={{ fontWeight: '600' }}>
                        €{formatNumber(totals.conversionsValue, 2)}
                        {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
                      </td>
                    )}
                    {isComponentMetricSelected('cos') && (
                      <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'search_terms_ngram')}`} style={{ fontWeight: '600' }}>
                        {formatNumber(totals.cos, 1)}%
                        {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
                      </td>
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedNgrams.length)} sur {sortedNgrams.length} {selectedNgramSize === 1 ? 'mots' : selectedNgramSize === 2 ? 'bigrammes' : 'trigrammes'}
                </span>
              </div>
              <div className="pagination-buttons">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="search_terms_ngram"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Analyse N-gram"
      />
    </div>
  );
});

NgramAnalysisTable.displayName = 'NgramAnalysisTable';

export default NgramAnalysisTable;